import { forkJoin, Observable, from } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { deepObjectAssign } from '../util/helpers';

declare const document: any;

export function ModuleTranslateLoaderFactory(http: HttpClient, options?: Record<string, string>): TranslateLoader {
	const { modulesJsonPath, i18nAssetPath, fileSuffix, fallbackLang } = options || {};
	return new ModuleTranslateLoader(http, modulesJsonPath, i18nAssetPath, fileSuffix, fallbackLang);
}

export class ModuleTranslateLoader implements TranslateLoader {
	private readonly base: string = '';
	private readonly slashlessBase: string = ''; // base tag value without ending in '/'

	constructor(
		protected http: HttpClient,
		protected modulesJsonPath: string = '/modules/modules.json',
		protected i18nAssetPath: string = '/assets/i18n/',
		protected fileSuffix: string = '.json',
		protected fallbackLang: string = 'en'
	) {
		const baseElement = document.querySelector('base');
		if (!!baseElement?.getAttribute('href')?.length) {
			// If the base tag points to anything but the standard '/' reroute all i18n asset fetches there.
			this.base = baseElement.getAttribute('href') || '';
			this.slashlessBase = this.base.endsWith('/')
				? this.base.slice(0, this.slashlessBase.lastIndexOf('/'))
				: this.base;

			this.fileSuffix = fileSuffix;
		}
	}

	getTranslation(lang: string): Observable<any> {
		return this.http.get(`${this.slashlessBase}${this.i18nAssetPath}${lang}${this.fileSuffix}`).pipe(
			switchMap((base) => this.loadAndMergeModuleTranslationsIntoBase(base, lang)),
			catchError(() => {
				return from(this.loadAndMergeModuleTranslationsIntoBase({}, lang));
			})
		);
	}

	private loadAndMergeModuleTranslationsIntoBase(base: any, lang: string): Observable<any> {
		// Create a deep copy of the base to avoid mutating the original object
		const mergedTranslation = { ...base };

		return this.http.get<{ modules: string[] }>(`${this.slashlessBase}${this.modulesJsonPath}`).pipe(
			switchMap((resModules) => {
				const moduleScopedRequests = resModules.modules.map((module) =>
					this.requestModuleTranslation(lang, module).pipe(
						catchError(() => {
							console.warn(
								`Unable to retrieve scoped translations for module: "${module}" lang: "${lang}". Requesting fallback lang`
							);
							return from(
								this.requestModuleTranslation(this.fallbackLang, module).pipe(
									catchError(() => {
										console.warn(
											`Unable to retrieve scoped translations for module: "${module}" fallback lang: "${lang}"`
										);
										return from([null]);
									})
								)
							);
						})
					)
				);

				const moduleGeneralRequests = resModules.modules.map((module) =>
					this.requestModuleGeneralTranslation(lang, module).pipe(
						catchError(() => {
							console.warn(
								`Unable to retrieve general translations for module: "${module}" lang: "${lang}"`
							);
							return from([null]);
						})
					)
				);

				return forkJoin([forkJoin(moduleScopedRequests), forkJoin(moduleGeneralRequests)]).pipe(
					map(([scopedTranslations, generalTranslations]) => {
						// First, merge all scoped translations
						for (let i = 0; i < resModules.modules.length; i++) {
							const moduleKey = `_${resModules.modules[i]}`;
							if (scopedTranslations[i]) {
								mergedTranslation[moduleKey] = deepObjectAssign(
									{ ...scopedTranslations[i] }, // Avoid modifying the original object
									mergedTranslation[moduleKey] ? { ...mergedTranslation[moduleKey] } : {}
								);
							}
						}

						// Then, merge all general translations (these will override any conflicting keys)
						for (let i = 0; i < resModules.modules.length; i++) {
							if (generalTranslations[i]) {
								deepObjectAssign(mergedTranslation, generalTranslations[i]);
							}
						}

						return mergedTranslation;
					})
				);
			}),
			catchError((err, caught) => {
				console.warn(err);
				return from([mergedTranslation]);
			})
		);
	}

	private requestModuleTranslation(lang: string, module: string): Observable<any> {
		return this.http.get(`${this.slashlessBase}/modules/${module}/assets/i18n/${lang}${this.fileSuffix}`);
	}

	private requestModuleGeneralTranslation(lang: string, module: string): Observable<any> {
		return this.http.get(`${this.slashlessBase}/modules/${module}/assets/i18n/general/${lang}${this.fileSuffix}`);
	}
}
